import { useRef, useEffect } from 'react'
import { requestFrame, cancelFrame, frameNow, FRAME_LAYER } from '../utils/frameDriver'

/**
 * OrderflowTrail — ATAS-style live trade-flow trail.
 *
 * Two jagged trails (bid / ask) that LAUNCH from the price level of the
 * footprint cell that just traded and STREAM RIGHTWARD into the y-axis
 * gutter, away from the footprint (which flows right-to-left). Volume dots
 * ride each vertex, radius ∝ √volume. Clock-driven: a requestAnimationFrame
 * loop repaints every frame against the current wall clock, so the trail
 * flows continuously even between trades — x = origin + (now - arrival)*pxPerMs.
 *
 * SIMPLIFIED (post-heartbeat): the server-clock rebase and the slot-boundary
 * lead hold are GONE. Both were compensations built while hunting the ~1Hz
 * heartbeat, whose root cause turned out to live elsewhere entirely
 * (MessagesPanel's per-row Intl construction). Left in place they became the
 * bug: skew in the smoothed server offset made fresh trades clamp to age 0
 * (every new dot piling ON the forming candle) or inflated every age (the
 * whole stream lagging the live edge), and the hold froze the lead at the
 * last dot whenever trade ts and slot start disagreed across that same
 * offset. x runs on the ARRIVAL clock again — one clock, no offset to drift.
 *
 * The repaint is CAPPED to TRAIL_FPS (below): rAF fires at the monitor's
 * refresh rate, and repainting the whole trail 60–144×/s pinned a CPU core
 * even on a dead token. The flow is a slow drift, so a lower cap is visually
 * identical. The loop also PAUSES while the tab is hidden.
 *
 * VENUE-AGNOSTIC: feed is `trades` (data.recentTrades), which exists
 * identically for Solana swaps and HL futures. `priceToRow` maps the
 * trade's price/mcap to a row exactly as the footprint does, so a trail
 * point launches seamlessly from its originating cell on either venue.
 *
 * CRITICAL MOUNTING NOTES (hard-won from this codebase):
 *  - LTR container. The footprint lives in a double-reversed RTL wrapper;
 *    this must NOT inherit that or the rightward x-math gets mirrored.
 *    The host sets `direction: ltr` explicitly.
 *  - Trail is PINNED like the canvas: each frame reads pinPx from getGeom()
 *    (lastStyleRef committed paint), never re-derives residual mid-frame and
 *    never adds scrollTop — so vertices stay locked to cell Y.
 *
 * Props:
 *   trades        data.recentTrades (newest-first; each { ts, side, mcap, amount })
 *   priceToRow    (price) => row index (fractional ok) — footprint geometry
 *   rowHeight     px per row (dynamicRowHeight)
 *   liveEdgeX     x of the live edge (footprint's right edge) in px
 *   width         px width of the flow gutter (live edge → right of canvas)
 *   height        canvas height (chart body height)
 *   getGeom       () => { rowHeight, pinPx, livePrice, ... } — pinPx is the pinned Y
 *                 translate (committed lastStyleRef); prefer over any prop
 *   windowMs      how many ms of flow are visible across `width` (scroll speed)
 *   theme         { up, down, outline } colors (outline = bubble ring, 6-digit hex)
 *   active        render only while visible (skips the RAF when false)
 */
// Drop shadow under the trail (lines + bubbles). Tweak here. CSS px; alpha
// is the shadow colour's opacity. Drawn as ONE pass per frame, before the
// trail, under the same transform: the trail geometry translated (dx, dy)
// in translucent black, softened by a zero-offset blur — two draw calls per
// frame (all lines in one path, all bubbles in another).
const TRAIL_SHADOW = { dx: 2, dy: 15, blur: 4, alpha: 0.9 }
// phonelag-20261102: hard cap on the trail's own history.
const TRAIL_CAP = (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches) ? 5000 : 20000

export default function OrderflowTrail({
  trades,
  priceToRow,
  rowHeight,
  getGeom,
  flowDir = 'left',
  hovered = false,
  liveEdgeX,
  // The price the candle body and the dashed line are drawn at this frame
  // (resolveLivePriceForY, stamped in drawCanvas). Anchors the lead-in of
  // whichever side printed last. 0 / absent = behave exactly as before.
  liveMcap = 0,
  // price -> y in PAINT space, from the chart's own projection. Snapped to
  // the cell grid or continuous — FPCC decides, per frame, the same way it
  // decides for everything else it draws. Absent = legacy priceToRow path.
  priceToY = null,
  getOriginX,
  width,
  height,
  regionPx = 0, // transitional fallback only; prefer getGeom().pinPx
  windowMs = 30000,
  // Fraction of the gutter the tail may reach (1 = full width, as before).
  tailFrac = 1,
  // Tail length in px back from the live point (overrides tailFrac). No cap.
  tailPx = null,
  theme = { up: '#26a69a', down: '#ef5350', outline: '#a0a0a0' },
  interactingRef, // while true: sleep idle clock; FPCC paintHook owns frames
  paintHookRef,   // FPCC assigns; force wake+paint after interact / full draws
  active = true,
  // Draw each bubble's volume as text (trail menu > Bubbles > NUMBERS).
  showLabels = false,
  // Size curve exponent: r = MIN + (MAX-MIN) * frac^sizeCurve. 0.5 = old sqrt, 1 = linear.
  sizeCurve = 0.85,
  // trailvol-20261103: ref to the chart's typical (median) visible candle volume.
  candleVolRef,
}) {
  const canvasRef = useRef(null)
  // ─── Followed wallets ───
  // Reads THE app's watchlist — 'solcheddar_watchlist', the array
  // WalletsPanel writes and TapePanel highlights from. It previously read a
  // separate 'cheddar_followed_wallets' object that nothing in the app ever
  // wrote, so the feature was structurally dead: one fact, two stores.
  //
  // Live, not mount-once: a wallet added mid-session used to require a
  // reload before the trail knew. Read from storage on a slow poll rather
  // than plumbed as a prop — FPCC deliberately stopped referencing the
  // watchlist when the Wallets tab moved out, and threading it back through
  // the chart to reach a leaf would undo that. A wallet list changes a few
  // times a session; 2s is invisible, and the raw string is compared before
  // parsing so an unchanged list costs one localStorage read. The 'storage'
  // event covers OTHER tabs (all it fires for); the interval covers this one.
  //
  // PREFIX matching, like TapePanel: the trade feed can carry a truncated
  // wallet, so an exact key lookup silently misses. 8 chars is the same
  // threshold the tape uses — one rule, one behaviour.
  const followedRef = useRef([])
  useEffect(() => {
    let last = ''
    const load = () => {
      let raw = ''
      try { raw = localStorage.getItem('solcheddar_watchlist') || '[]' } catch { return }
      if (raw === last) return              // unchanged: skip the parse entirely
      last = raw
      try {
        const arr = JSON.parse(raw)
        followedRef.current = Array.isArray(arr) ? arr.filter(w => w && w.address) : []
      } catch { followedRef.current = [] }
    }
    load()
    const id = setInterval(load, 2000)
    window.addEventListener('storage', load)
    return () => { clearInterval(id); window.removeEventListener('storage', load) }
  }, [])
  // address prefix -> label, resolved per trade
  const followCacheRef = useRef({ list: null, map: new WeakMap() })
  const followOf = (t) => {
    const C = followCacheRef.current
    if (C.list !== followedRef.current) { C.list = followedRef.current; C.map = new WeakMap() }
    if (C.map.has(t)) return C.map.get(t)
    const v = C.list.length ? followLabel(t.wallet) : undefined
    C.map.set(t, v)
    return v
  }
  const followLabel = (wallet) => {
    if (!wallet || wallet === 'unknown') return undefined
    const list = followedRef.current
    for (let i = 0; i < list.length; i++) {
      const a = list[i].address
      if (a && wallet.startsWith(a.slice(0, 8))) {
        return list[i].label || `${a.slice(0, 4)}…${a.slice(-4)}`
      }
    }
    return undefined
  }
  const rafRef = useRef(0)
  // Last seen row per side — lets the flat deadline keep printing even when
  // a side has had NO trade in the whole visible window (empty pts).
  const lastRowRef = useRef({ buy: null, sell: null })
  // Decaying-max volume reference for dot sizing. t.amount's SCALE varies by
  // orders of magnitude across venues/tokens (SOL per swap vs futures size),
  // so the old absolute √vol×0.4 mapping pinned every realistic Solana print
  // (0.05–5 SOL) below its 1.5px floor — all dots identical. Sizing is now
  // RELATIVE to the biggest print seen recently: ref = max(frame max,
  // ref×0.995/frame) — half-life ≈ 5s at 30fps — so the encoding uses the
  // full 1.5–6px range on any token and self-recalibrates within seconds of
  // a token switch, with no per-venue constants.
  const volRefRef = useRef(0)
  const shadowMaskRef = useRef(null)   // offscreen mask for the uniform trail shadow
  // (dot-order) Reused index buffer for the size-sorted dot pass.
  const dotOrderRef = useRef([])
  const spriteRef = useRef(new Map())   // bubble sprite cache
  const lastPaintAtRef = useRef(0)
  // Latest props read inside the RAF loop without re-subscribing each frame.
  const stateRef = useRef({})
  // smokepx2: own trail history so the tail can outlive the 200-350 trade
  // recentTrades buffer. Merge new trade objects, prune by tail age.
  // phonelag-20261102: append-only buffer, oldest -> newest, with a start
  // pointer for drops. No per-update sort or full copy. Compacted only when
  // the dead head outgrows the live part (amortised O(1)).
  const histRef = useRef({ buf: [], start: 0, seen: new WeakSet() })
  {
    const H = histRef.current, inc = trades || []
    const reset = () => { H.buf = []; H.start = 0; H.seen = new WeakSet() }
    if (inc.length === 0) reset()
    else {
      const fresh = []
      for (let i = 0; i < inc.length; i++) { const x = inc[i]; if (x && typeof x === 'object' && !H.seen.has(x)) fresh.push(x) }
      if (fresh.length === inc.length && H.buf.length - H.start > 0 && inc.length > 20) reset()   // coin switch
      if (fresh.length) {
        // Only the new batch is ordered (small); then appended.
        fresh.sort((a, b) => (a.arrivalTs || 0) - (b.arrivalTs || 0))
        for (let i = 0; i < fresh.length; i++) { H.seen.add(fresh[i]); H.buf.push(fresh[i]) }
        const pxPerMs0 = Math.max(1, width || 1) / Math.max(1, windowMs || 30000)
        const keepMs = ((tailPx || 0) > 0 ? tailPx / pxPerMs0 : (windowMs || 30000)) + 5000
        const cut = Date.now() - keepMs
        const cap = TRAIL_CAP
        const buf = H.buf
        let st = H.start
        while (st < buf.length && (buf[st].arrivalTs || 0) < cut) st++
        if (buf.length - st > cap) st = buf.length - cap
        if (st > 4096 && st > (buf.length - st)) { H.buf = buf.slice(st); st = 0 }
        H.start = st
      }
    }
  }
  const trailTrades = histRef.current
  stateRef.current = { trades: trailTrades, tailPx, priceToRow, priceToY, rowHeight, getGeom, flowDir, hovered, liveEdgeX, liveMcap, getOriginX, width, height, regionPx, windowMs, tailFrac, theme, showLabels, sizeCurve }

  useEffect(() => {
    if (!active) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')

    // ── Repaint cap ──
    // rAF fires at the MONITOR's refresh rate (60Hz, or 120/144Hz on a
    // high-refresh display). The old loop repainted the ENTIRE trail on every
    // one of those frames, forever — including on a dead token, where the only
    // thing drawn is two static deadline lines. That pinned a whole CPU core
    // (the "idle chart still at ~100% CPU" bug). The flow is a slow drift
    // (~windowMs to cross the gutter), so 30fps looks identical and cuts the
    // per-frame canvas work 2×–5× depending on the display. Raise TRAIL_FPS
    // for a silkier trail at the cost of more CPU.
    const TRAIL_FPS = 30
    const FRAME_MS = 1000 / TRAIL_FPS
    let prevTs = 0           // previous rAF timestamp (display-period estimate)
    let emaDelta = 1000 / 60 // display frame period, slow EMA — seed 60Hz until measured
    let frameCount = 0
    // Geometry of the last FULL paint — lets a drag frame shift the existing
    // pixels instead of redrawing every dot. Null until the first paint.
    let lastPaint = null

    // Full trail paint body (extracted so paintHook can run it without the clock).
    // Never hidden during drag. It used to go to opacity 0 for the whole
    // gesture, which on phone blinked it on every touch. It doesn't need
    // hiding: during interaction FPCC paints it via paintHook at the end of
    // every full drawCanvas pass, from the same frame the chart just drew.
    const paintFrame = (ts) => {
      lastPaintAtRef.current = performance.now()
      const s = stateRef.current
      // ── Drag/zoom fast path ────────────────────────────────────────────
      // Every dot sits at originNow + dir*age*pxPerMs, and pxPerMs is fixed
      // by windowMs and the canvas width — so while ONLY the origin moves,
      // this frame is the last one shifted sideways. Redrawing all 350 dots
      // per drag frame is what made the chart lag during an x-drag; the old
      // code masked it by hiding the trail, whose early return skipped the
      // paint entirely. Translating the layer is pixel-exact and costs a
      // compositor offset. Any change to the Y lattice, row height or pin
      // falls through to a real paint, because a shift can't express those.
      if (interactingRef?.current && lastPaint && typeof s.getOriginX === 'function') {
        const g = (typeof s.getGeom === 'function') ? s.getGeom() : null
        const ox = s.getOriginX()
        if (g && Number.isFinite(ox)
            && g.latticeId === lastPaint.latticeId
            && g.rowHeight === lastPaint.rowHeight
            && g.pinPx     === lastPaint.pinPx
            // (resize-flash) A panel resize also changes the box; a sideways
            // shift can't express that, so it must take the real paint.
            && s.width     === lastPaint.width
            && s.height    === lastPaint.height) {
          const d = ox - lastPaint.originX
          const next = d ? `translateX(${d}px)` : ''
          if (canvas.style.transform !== next) canvas.style.transform = next
          return
        }
      }
      if (canvas.style.transform) canvas.style.transform = ''
      const dpr = window.devicePixelRatio || 1
      const w = Math.max(1, s.width)
      const h = Math.max(1, s.height)
      // Round before comparing — canvas.width is an integer and w is not
      // (liveEdge = containerWidth - RIGHT_PADDING - axisW). Comparing the
      // two directly was always true, so the bitmap was reallocated and
      // implicitly cleared on every paint, and the compositor rebuilt the
      // layer each time. FPCC's grid and volume canvases already round; these
      // two layers were the ones that missed it.
      const cvW = Math.max(1, Math.round(w * dpr))
      const cvH = Math.max(1, Math.round(h * dpr))
      if (canvas.width !== cvW || canvas.height !== cvH) {
        canvas.width = cvW
        canvas.height = cvH
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, w, h)

      // Client wall clock for ages: arrivalTs is Date.now() at ingest (dumps
      // synthesize on the same epoch). frameNow() stays on that epoch. The
      // server-app clock (__cheddarNow) jumps on ping sync / offset rebase
      // while existing arrivalTs do not — age ≈ skew (~15s) put every dot
      // ~half a 30s window LEFT of the live candle. One client clock, no jump.
      const now = frameNow()
      const pxPerMs = w / s.windowMs
      const H = s.trades || { buf: [], start: 0 }
      const hb = H.buf, h0 = H.start
      // Live candle x, fresh this frame (tracks scroll/zoom; no render lag).
      const originNow = (typeof s.getOriginX === 'function') ? s.getOriginX() : w
      // Atomic per-frame geometry (rowHeight + pinPx from lastStyleRef via
      // getGeom). Trail is PINNED — pinPx never includes scrollTop. Falls
      // back to regionPx / prop only during transition.
      const geom = (typeof s.getGeom === 'function')
        ? s.getGeom()
        : { rowHeight: s.rowHeight, pinPx: s.regionPx, regionPx: s.regionPx }
      const RH = geom.rowHeight
      lastPaint = {
        originX:   originNow,
        latticeId: geom.latticeId,
        rowHeight: RH,
        pinPx:     geom.pinPx,
        width:     s.width,
        height:    s.height,
      }
      const camS = Number.isFinite(geom.camScale) ? geom.camScale : 1
      const camT = Number.isFinite(geom.camTy) ? geom.camTy : 0
      const REG = Number.isFinite(geom.pinPx) ? geom.pinPx
        : (Number.isFinite(geom.regionPx) ? geom.regionPx : s.regionPx)
      // Flow direction: 'right' ages trades rightward from the forming candle,
      // 'left' (default) ages leftward into the chart body. One sign, flipped
      // by the right-click menu and read fresh each frame via stateRef.
      const dir = s.flowDir === 'right' ? 1 : -1
      // Hover glow (AVWAP-style): when the live-edge handle is hovered, the
      // whole trail brightens — thicker lines + a soft shadow halo. Read once
      // per frame from stateRef so it tracks the host's hover state live.
      const hov = !!s.hovered
      const lineW = hov ? 2.5 : 1.5
      const glowBlur = hov ? 8 : 0
      // Map a trade to a flow point. x grows AWAY from the live edge as the
      // trade ages (direction set by dir). y is the trade's price row, plus
      // the shared region transform so it tracks the cells.
      // Clock base for x: ARRIVAL time, one client clock
      // age = frameNow - arrivalTs (both Date.now() epoch). Newest at origin.
      // No fallback to t.ts (exchange) — that vs wall clock is the half-screen
      // left shift. Clamp negatives so a frame-vs-stamp jitter cannot push
      // dots past the live candle.
      const ageOf = (t) => {
        if (t.arrivalTs == null) return null
        return Math.max(0, now - t.arrivalTs)
      }
      // THE one price->pixel conversion in this file. Everything that needs
      // a y calls this, including the live-edge anchor — a second copy of
      // the formula is how the anchor ended up on a different grid from the
      // dots it was supposed to join. camS/camT/REG are paint-space and
      // stay here; the projection maps price to paint space and stops.
      const yOf = (mcap) => {
        if (s.priceToY) {
          const yy = s.priceToY(mcap)
          return Number.isFinite(yy) ? yy * camS + camT + REG : null
        }
        const row = s.priceToRow(mcap)
        if (row == null || !Number.isFinite(row)) return null
        return (row * RH + RH / 2) * camS + camT + REG
      }
      const toPoint = (t) => {
        const age = ageOf(t)
        if (age == null) return null
        // Newest trade launches at the LIVE (forming) CANDLE's exact x
        // (originNow, computed fresh this frame) and ages per `dir`.
        const x = originNow + dir * age * pxPerMs
        // Out-of-lattice prices keep their direction and draw clipped; only
        // a non-finite result is a cull.
        const y = yOf(t.mcap)
        if (y == null) return null
        const fname = followOf(t)
        return { x, y, vol: t.amount, side: t.side, isOwn: !!t.isOwn, follow: fname }
      }

      // Build the two trails newest→oldest until they run off the gutter.
      const bid = []
      const ask = []
      let bidOut = false, askOut = false
      for (let i = hb.length - 1; i >= h0; i--) {
        const t = hb[i]
        const age = ageOf(t)
        if (age == null) continue
        if (age > (s.tailPx > 0 ? s.tailPx / pxPerMs : s.windowMs * (s.tailFrac > 0 ? Math.min(1, s.tailFrac) : 1))) break               // older than the window → stop
        // Cull: once a side has one point past the far edge (so its line
        // exits cleanly), older trades on that side are off-screen too.
        const isBuy = t.side === 'buy'
        if (isBuy ? bidOut : askOut) { if (bidOut && askOut) break; continue }
        const p = toPoint(t)
        if (!p) continue
        if (isBuy) bid.push(p)
        else ask.push(p)
        if (dir === -1 ? p.x < -24 : p.x > w + 24) { if (isBuy) bidOut = true; else askOut = true }
      }

      // Update the dot-size reference: decayed max over BOTH sides, so a big
      // ask out-sizes small bids and vice versa. Decay-only frames (no
      // trades) let the ref relax instead of freezing at a stale whale print.
      {
        let rawMax = 0
        for (const p of bid) { if (Number.isFinite(p.vol) && p.vol > rawMax) rawMax = p.vol }
        for (const p of ask) { if (Number.isFinite(p.vol) && p.vol > rawMax) rawMax = p.vol }
        volRefRef.current = Math.max(rawMax, volRefRef.current * 0.995)
      }
      const volRef = volRefRef.current

      // ── LIVE-EDGE ANCHOR ──────────────────────────────────────────────
      // Same row→pixel form as toPoint, so it lands on the body and the
      // dashed line by construction rather than by coincidence. Side comes
      // from the newest trade in the list (newest-first), never from
      // comparing x — `dir` flips the ageing direction and would invert it.
      let yLive = null
      let liveSide = null
      // PAINT-TIME, from the chart's own frame. The liveMcap prop is a render
      // snapshot and was never actually passed, so this branch was dead and
      // the lead-in fell back to the newest DOT's price — which is last
      // trade, not the resolved live price the dashed line is drawn at.
      const liveM = (geom && geom.livePrice > 0) ? geom.livePrice : s.liveMcap
      if (liveM > 0) {
        yLive = yOf(liveM)
        if (yLive != null) {
          { const nt = hb.length > h0 ? hb[hb.length - 1] : null; liveSide = nt?.side === 'buy' ? 'buy' : (nt?.side ? 'sell' : null) }
        }
      }

      // Bubble radius: biggest recent print → 12px, quarter-size → ~7.5px
      // (√ keeps dot AREA ∝ volume), dust rides the 3px floor.
      // trailvol-20261103: size is relative to CHART volume — trade size /
      // median volume of the visible completed candles at the current
      // timeframe. A print equal to a typical candle's whole volume hits the
      // 18px cap; 1/4 of a candle ≈ 6px at k=0.85. Falls back to the old
      // decaying-max-of-trail reference only until the chart has candles.
      const _cv = candleVolRef?.current
      const sizeRef = _cv > 0 ? _cv : volRef
      const radiusOf = (p) => {
        const frac = (sizeRef > 0 && Number.isFinite(p.vol) && p.vol > 0) ? Math.min(1, p.vol / sizeRef) : 0
        const k = (s.sizeCurve > 0) ? s.sizeCurve : 0.85
        return Math.min(18, 1.5 + 16.5 * Math.pow(frac, k))
      }

      // ── DROP SHADOW (TRAIL_SHADOW) ─────────────────────────────────────
      // A real draw of the same geometry, translated (dx, dy) under the SAME
      // transform as the trail, in translucent black, softened by a zero-
      // offset canvas shadow of the same colour. Nothing is drawn off-canvas:
      // the previous "paint off-screen, let shadowOffset bring the shadow
      // back" trick assumed x - (w + 64) is always off the bitmap, but trail
      // points can sit past `w` after scaling, so the "off-screen" shapes
      // landed ON screen as a crisp opaque black copy shifted left. Here every pixel is the shadow.
      // One stroke + one fill per frame, inside save/restore.
      {
        const SH = TRAIL_SHADOW
        const lines = new Path2D()
        const dots = new Path2D()
        const addSide = (pts, anchorY) => {
          if (pts.length === 0) return
          lines.moveTo(originNow, anchorY != null ? anchorY : pts[0].y)
          for (let i = 0; i < pts.length; i++) lines.lineTo(pts[i].x, pts[i].y)
          for (const p of pts) {
            if (p.x < -30 || p.x > w + 30 || p.y < -30 || p.y > h + 30) continue
            const r = radiusOf(p)
            dots.moveTo(p.x + r, p.y)
            dots.arc(p.x, p.y, r, 0, Math.PI * 2)
          }
        }
        const addDeadline = (storedY) => {
          if (storedY == null) return
          lines.moveTo(originNow, storedY + REG)
          lines.lineTo(dir === 1 ? w : 0, storedY + REG)
        }
        addSide(ask, liveSide === 'sell' ? yLive : null)
        addSide(bid, liveSide === 'buy' ? yLive : null)
        if (ask.length === 0) addDeadline(lastRowRef.current.sell)
        if (bid.length === 0) addDeadline(lastRowRef.current.buy)
        // One uniform shadow: lines + dots drawn OPAQUE into an offscreen
        // mask, then composited once with blur + alpha. Before, the line
        // stroke and dot fill were separate translucent draws, so lines read
        // fainter than dots and darkened where they overlapped.
        const cv = ctx.canvas
        let m = shadowMaskRef.current
        if (!m) { m = document.createElement('canvas'); shadowMaskRef.current = m }
        if (m.width !== cv.width || m.height !== cv.height) { m.width = cv.width; m.height = cv.height }
        const mc = m.getContext('2d')
        mc.setTransform(1, 0, 0, 1, 0, 0)
        mc.clearRect(0, 0, m.width, m.height)
        mc.setTransform(ctx.getTransform())
        mc.translate(SH.dx, SH.dy)           // CSS px, under the dpr transform
        mc.strokeStyle = '#000'
        mc.fillStyle = '#000'
        mc.lineWidth = lineW
        mc.lineJoin = 'round'
        mc.lineCap = 'round'
        mc.stroke(lines)
        mc.fill(dots)
        ctx.save()
        ctx.setTransform(1, 0, 0, 1, 0, 0)
        ctx.globalAlpha = SH.alpha
        ctx.filter = `blur(${(SH.blur * dpr) / 2}px)`
        ctx.drawImage(m, 0, 0)
        ctx.restore()
      }

      // Newest point sits at the live edge (x=w). pts are newest→oldest, so
      // pts[0] is the most recent for that side. When no trade has landed
      // this frame, the line still extends FROM the newest point TO the live
      // edge as a flat segment at that last price — a continuous deadline
      // that keeps printing rightward while the tape is quiet (EKG style).
      const stroke = (pts, color, anchorY = null) => {
        if (pts.length === 0) return
        ctx.lineWidth = lineW
        ctx.strokeStyle = color
        ctx.shadowBlur = glowBlur
        ctx.shadowColor = hov ? color : 'transparent'
        ctx.beginPath()
        // EKG lead-in: a flat segment from the forming candle's CENTER
        // (originNow) back to the newest dot's price — this keeps the trail
        // printing continuously, including while orders are slow.
        // Lead-in starts at the resolved live price for the side that
        // printed last; the quiet side still runs flat from its own dot.
        ctx.moveTo(originNow, anchorY != null ? anchorY : pts[0].y)
        ctx.lineTo(pts[0].x, pts[0].y)
        for (let i = 0; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y)
        ctx.stroke()
        ctx.shadowBlur = 0
        ctx.shadowColor = 'transparent'
      }
      // (dot-order) One dot + its outline (+ own/follow ring and label).
      // Drawn in a pass of their own AFTER every line, so the two lines can
      // cross freely underneath and no line ever cuts over a bubble.
      const drawDot = (p, color) => {
        {
          const r = radiusOf(p)
          if (p.x + r < 0 || p.x - r > w || p.y + r < 0 || p.y - r > h) return
          // Sprite: filled dot + 1-device-px outline (0.9 alpha), cached per colour/radius.
          const rq = Math.round(r * 4) / 4
          const outC = s.theme.outline || '#a0a0a0'
          const key = color + outC + rq + '|' + dpr
          let spr = spriteRef.current.get(key)
          if (!spr) {
            if (spriteRef.current.size > 600) spriteRef.current.clear()
            const half = rq + 1
            const px = Math.ceil(half * 2 * dpr)
            spr = document.createElement('canvas'); spr.width = px; spr.height = px
            const sc = spr.getContext('2d')
            sc.setTransform(dpr, 0, 0, dpr, 0, 0)
            sc.beginPath(); sc.arc(half, half, rq, 0, Math.PI * 2)
            sc.fillStyle = color; sc.fill()
            sc.strokeStyle = outC; sc.globalAlpha = 0.9; sc.lineWidth = 1 / dpr; sc.stroke()
            spr._h = half; spr._s = px / dpr
            spriteRef.current.set(key, spr)
          }
          ctx.drawImage(spr, p.x - spr._h, p.y - spr._h, spr._s, spr._s)
          // Own filled order: yellow dashed ring around the volume dot at the
          // exact point it transacted, so you can spot your own fill in the
          // flow. Sits outside the dot (r + padding), dashed, no shadow.
          if (p.isOwn || p.follow) {
            // Own fill: YELLOW ring, amount bought/sold above.
            // Followed wallet: BLUE ring, wallet name above.
            // Label halo-first (dark stroke) so it reads over heat, cells,
            // or the void alike.
            const ringCol = p.isOwn ? '#ffd000' : '#448aff'
            const label = p.isOwn
              ? (p.vol >= 1e6 ? `${(p.vol / 1e6).toFixed(1)}M` : p.vol >= 1e3 ? `${(p.vol / 1e3).toFixed(1)}K` : `${Math.round(p.vol)}`)
              : p.follow
            ctx.save()
            ctx.beginPath()
            ctx.strokeStyle = ringCol
            ctx.lineWidth = 1.5
            ctx.setLineDash([3, 3])
            ctx.arc(p.x, p.y, r + 4, 0, Math.PI * 2)
            ctx.stroke()
            ctx.setLineDash([])
            ctx.font = "600 9px 'JetBrains Mono', monospace"
            ctx.textAlign = 'center'
            ctx.lineWidth = 3
            ctx.strokeStyle = 'rgba(0,0,0,0.85)'
            ctx.strokeText(label, p.x, p.y - r - 7)
            ctx.fillStyle = p.isOwn ? '#ffd000' : '#7ab0ff'
            ctx.fillText(label, p.x, p.y - r - 7)
            ctx.restore()
          }
        }
      }

      // Update remembered last row from the newest point each side has.
      if (bid.length) lastRowRef.current.buy = bid[0].y - REG
      if (ask.length) lastRowRef.current.sell = ask[0].y - REG

      // Pure deadline when a side is empty this window: a flat line across
      // the whole chart at its last known price (re-applying pinPx so it
      // still tracks residual/drag). Keeps both lines continuously printing.
      const deadline = (storedY, color) => {
        if (storedY == null) return
        const y = storedY + REG
        ctx.lineWidth = lineW
        ctx.strokeStyle = color
        ctx.shadowBlur = glowBlur
        ctx.shadowColor = hov ? color : 'transparent'
        ctx.globalAlpha = 0.55
        ctx.beginPath()
        ctx.moveTo(originNow, y)        // from the forming candle's center…
        ctx.lineTo(dir === 1 ? w : 0, y) // …toward the flow direction's edge
        ctx.stroke()
        ctx.shadowBlur = 0
        ctx.shadowColor = 'transparent'
        ctx.globalAlpha = 1
      }
      if (ask.length === 0) deadline(lastRowRef.current.sell, s.theme.down)
      if (bid.length === 0) deadline(lastRowRef.current.buy, s.theme.up)

      stroke(ask, s.theme.down, liveSide === 'sell' ? yLive : null)
      stroke(bid, s.theme.up,   liveSide === 'buy'  ? yLive : null)

      // (dot-order) Dots from BOTH sides, smallest first, so a bigger bubble
      // always sits on top of a smaller one whichever side it is. Radius is
      // monotonic in volume, so the order is by volume; ties keep time order
      // (newest first), which Array.sort guarantees (stable). One index sort
      // per paint over a few hundred points.
      {
        const nA = ask.length, nAll = nA + bid.length
        if (nAll) {
          const ord = dotOrderRef.current.length >= nAll
            ? dotOrderRef.current : (dotOrderRef.current = new Array(nAll))
          ord.length = nAll
          for (let i = 0; i < nAll; i++) ord[i] = i
          const volAt = (i) => {
            const v = (i < nA ? ask[i] : bid[i - nA]).vol
            return Number.isFinite(v) ? v : 0
          }
          ord.sort((i, j) => volAt(i) - volAt(j))
          const upC = s.theme.up, downC = s.theme.down
          for (let k = 0; k < nAll; k++) {
            const i = ord[k]
            if (i < nA) drawDot(ask[i], downC)
            else drawDot(bid[i - nA], upC)
          }
          // Bubble numbers: visible bubbles only, skip dust (r<3.5).
          // Inside when it fits, else just right of the bubble.
          if (s.showLabels) {
            const fmt = (v) => v >= 1e6 ? (v / 1e6).toFixed(1) + 'M' : v >= 1e3 ? (v / 1e3).toFixed(1) + 'k' : v >= 100 ? String(Math.round(v)) : v >= 1 ? v.toFixed(1) : v.toFixed(2)
            ctx.save()
            ctx.font = "600 9px 'JetBrains Mono', monospace"
            ctx.textBaseline = 'middle'
            ctx.lineWidth = 3
            ctx.strokeStyle = 'rgba(0,0,0,0.85)'
            ctx.fillStyle = '#fff'
            // Top ~15% of VISIBLE bubbles by size. ord is already sorted by
            // volume (ascending), so walk it from the biggest down: no extra sort.
            const vis = (p) => p.vol > 0 && p.x > -20 && p.x < w + 20 && p.y > -10 && p.y < h + 10
            let nVis = 0
            for (let k = 0; k < nAll; k++) { const i = ord[k]; if (vis(i < nA ? ask[i] : bid[i - nA])) nVis++ }
            let budget = Math.ceil(nVis * 0.15)
            ctx.textAlign = 'center'
            for (let k = nAll - 1; k >= 0 && budget > 0; k--) {
              const i = ord[k]
              const p = i < nA ? ask[i] : bid[i - nA]
              if (!vis(p)) continue
              budget--
              const r = radiusOf(p)
              const txt = fmt(p.vol)
              if (ctx.measureText(txt).width > r * 2 - 2) continue   // inside only, when it fits
              ctx.strokeText(txt, p.x, p.y)
              ctx.fillText(txt, p.x, p.y)
            }
            ctx.restore()
          }
        }
      }
    }

    const draw = (ts) => {
      // Sleep while interacting — FPCC paintHook owns those frames (paint-when-changed).
      // Re-arming forever kept frameDriver awake at 30fps for no-op work.
      if (interactingRef?.current) {
        rafRef.current = 0
        return
      }
      rafRef.current = requestFrame(draw, FRAME_LAYER)

      // ── Even pacing by frame DIVISOR, not time threshold ──
      // A ms-threshold gate aliases against the quantized rAF grid: 33.33ms
      // sits exactly on the 2-frame boundary at 60Hz, so every gate decision
      // was a knife-edge float compare — paints slipped to the 3rd frame and
      // the remainder catch-up then fired an early one (50ms/17ms pairs =
      // the visible heartbeat; measured ±12ms interval swing). Instead:
      // estimate the display's frame period (slow EMA, samples clamped so
      // hidden-tab gaps can't poison it) and paint every Nth rAF tick,
      // N = round(FRAME_MS / period). Uniform cadence BY CONSTRUCTION at any
      // refresh rate (60Hz→every 2nd, 120→4th, 144→5th; ±0.2ms at 60Hz in
      // sim). Under real main-thread congestion N adapts within ~20 frames.
      if (prevTs > 0) {
        const d = ts - prevTs
        if (d >= 3 && d <= 100) emaDelta = emaDelta * 0.95 + d * 0.05
      }
      prevTs = ts
      const N = Math.max(1, Math.round(FRAME_MS / emaDelta))
      frameCount++
      if (frameCount % N !== 0) return
      if (performance.now() - lastPaintAtRef.current < FRAME_MS * 0.6) return
      paintFrame(ts)
    }

    const hookPaint = (opts) => {
      paintFrame(opts && opts.ts)
      if (!interactingRef?.current) start()
    }
    if (paintHookRef) paintHookRef.current = hookPaint

    const start = () => { if (!rafRef.current) rafRef.current = requestFrame(draw, FRAME_LAYER) }
    const stop  = () => { cancelFrame(rafRef.current); rafRef.current = 0 }

    // Pause the whole loop while the tab is hidden. A background trading tab
    // was still burning a core on this animation — browsers don't always
    // throttle rAF hard enough in the background (and some builds not at all),
    // and there's nothing to see when the tab isn't visible anyway. Resume on
    // return.
    // Phone wake/reconnect: layout + origin can shift while rAF was stopped.
    // Drop lastPaint so the first visible frame is a full paint at the real
    // origin — a stale translateX from the frozen frame is the other half of
    // the "starts in a different place" glitch after reconnect.
    const onVisibility = () => {
      if (document.hidden) {
        stop()
      } else {
        lastPaint = null
        if (canvas.style.transform) canvas.style.transform = ''
        start()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)

    if (!document.hidden) start()
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      if (paintHookRef && paintHookRef.current === hookPaint) paintHookRef.current = null
      stop()
    }
  }, [active])

  return (
    <div
      className="orderflow-trail"
      style={{
        // Mounted INSIDE the footprint scroll container (alongside the
        // canvas). Matches the canvas box exactly: top:0 left:0, fixed
        // width = chart area. The scroll container's overflow clips it at
        // the y-axis boundary — same mechanism the footprint uses — so it
        // can never paint over the price labels, no width-guess needed.
        position: 'absolute',
        top: 0,
        left: 0,
        width: liveEdgeX,          // chart area width (clipped by parent overflow)
        height,
        direction: 'ltr',          // escape the wrapper's RTL scaffold
        pointerEvents: 'none',
        zIndex: 1,                 // under footprint canvas (z2), above vol-profile (z0)
        overflow: 'hidden',
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%', pointerEvents: 'none' }} />
    </div>
  )
}